/* eslint-disable */
import { useState } from 'react'
import { useModal } from '../../hooks/use-modal-store'
import { Button } from '../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import useMediaQuery from '../../hooks/useMediaQuery'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { Table, TableHeader, TableHead, TableBody, TableRow, TableCell } from '../ui/table'
import env from '../../env/env'
import { toCurrency } from '../../utils/CurrencyUtil'

const MIN_REDEMPTION_AMOUNT = 100

const getFundRedemptionAmount = (fund) => {
  if (fund.redemptionType === 'VALUE') {
    return fund.withdrawableAmount ?? 0
  }

  if (fund.redemptionType === 'UNITS') {
    const units = fund.withdrawableUnits ?? fund.qty ?? 0
    return units * (fund.nav ?? 0)
  }

  return fund.currentValue ?? (fund.units ?? 0) * (fund.nav ?? 0)
}

const getFundRedemptionLabel = (fund) => {
  if (fund.redemptionType === 'ALL_UNITS') {
    return 'Full redemption'
  }

  if (fund.redemptionType === 'UNITS') {
    const units = fund.withdrawableUnits ?? fund.qty
    return units ? `${units} units` : null
  }

  return null
}

const MFRedeemBreakdown = () => {
  const { isOpen, onClose, type, data } = useModal()
  const isModalOpen = isOpen && type === 'openMFRedeemBreakdown'
  const isDesktop = useMediaQuery('(min-width: 1024px)')
  const [isLoading, setIsLoading] = useState(false)

  const totalAmount =
    data?.amount ??
    data?.constituents?.reduce((acc, fund) => acc + getFundRedemptionAmount(fund), 0)

  const hasLowRedemptionAmount = data?.constituents?.some(
    (fund) => Math.floor(getFundRedemptionAmount(fund)) < MIN_REDEMPTION_AMOUNT,
  )

  const handleContinue = async () => {
    if (!data?.onContinue || hasLowRedemptionAmount) return

    try {
      setIsLoading(true)
      await data.onContinue()
      onClose()
    } finally {
      setIsLoading(false)
    }
  }

  const renderFundRow = (fund, index, isMobile = false) => {
    console.log('fund', fund)
    const redemptionLabel = getFundRedemptionLabel(fund)
    const redemptionAmount = getFundRedemptionAmount(fund)

    if (isMobile) {
      return (
        <TableRow key={index} className='border-none'>
          <TableCell className='font-medium flex gap-x-2 px-4'>
            <img
              src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
              alt={`${fund.amcCode} icon`}
              className='rounded-md h-12 w-12'
            />
            <div className='w-full'>
              <p className='font-semibold text-sm text-nowrap truncate max-w-[284px]'>
                {fund.schemeName}
              </p>
              <div className='flex justify-between'>
                {/* <span className='font-medium text-xs text-black-400'>
                  {redemptionLabel || fund.redemptionType}
                </span> */}
                <span className='font-semibold text-right text-sm'>
                  ₹ {toCurrency(Math.floor(redemptionAmount))}
                </span>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )
    }

    return (
      <TableRow key={index}>
        <TableCell className='font-medium flex gap-x-2 max-w-[432px]'>
          <img
            src={`${env.MF_LOGOS_URL}/${fund?.amcCode}.png`}
            alt={`${fund.amcCode} icon`}
            className='rounded-md h-12 w-12'
          />
          <div>
            <p className='text-clip font-semibold text-base text-nowrap truncate'>
              {fund.schemeName}
            </p>
            <span className='font-medium text-xs text-black-400'>
              {redemptionLabel || fund.redemptionType}
            </span>
          </div>
        </TableCell>
        <TableCell className='font-semibold text-right'>
          ₹ {toCurrency(Math.floor(redemptionAmount))}
        </TableCell>
      </TableRow>
    )
  }

  const renderMinAmountError = (className = '') =>
    hasLowRedemptionAmount ? (
      <p className={`text-sm text-error-500 font-medium ${className}`}>
        Minimum redemption amount per fund should be ₹{MIN_REDEMPTION_AMOUNT}
      </p>
    ) : null

  return (
    <>
      {isDesktop ? (
        <Dialog open={isModalOpen} onOpenChange={() => onClose()}>
          <DialogContent className='sm:max-w-[736px] font-DMSans p-6' showclose={true}>
            <DialogHeader>
              <DialogTitle className='text-start font-semibold text-xl text-[#2D2D2D]'>
                Redemption Breakdown
              </DialogTitle>
              <DialogDescription className='text-start text-black-400 text-sm font-DMSans font-medium'>
                This is how your withdrawal of{' '}
                <b className='text-black-500'>₹ {toCurrency(Math.floor(totalAmount))}</b> gets
                divided
              </DialogDescription>
            </DialogHeader>
            <div>
              <Table>
                <TableHeader className='bg-[#F4F7FE]'>
                  <TableRow className='border-none'>
                    <TableHead className='text-primary-400 w-[50%] pl-12 rounded-l-xl'>
                      Fund Name
                    </TableHead>
                    <TableHead className='text-primary-400 text-right rounded-r-xl'>
                      Amount
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.constituents?.map((fund, index) => renderFundRow(fund, index))}
                </TableBody>
              </Table>
              {renderMinAmountError('mt-3')}
            </div>
            <DialogFooter>
              <Button
                className='rounded-lg bg-primary-500 hover:bg-primary-400 disabled:bg-primary-200 w-full sm:w-auto'
                onClick={handleContinue}
                loading={isLoading}
                disabled={hasLowRedemptionAmount}
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : (
        <Sheet open={isModalOpen} onOpenChange={() => onClose()}>
          <SheetContent
            className='font-DMSans p-0 py-5 rounded-t-[24px] bottom-0'
            showClose={true}
            side='bottom'
          >
            <SheetHeader className='px-4'>
              <SheetTitle className='text-start font-semibold text-[#2D2D2D]'>
                Redemption Breakdown
              </SheetTitle>
              <SheetDescription className='text-start text-black-400 font-medium text-sm'>
                This is how your withdrawal of{' '}
                <b className='text-black-500'>₹ {toCurrency(Math.floor(totalAmount))}</b> gets
                divided
              </SheetDescription>
            </SheetHeader>

            <Table className='mt-6'>
              <TableBody>
                {data?.constituents?.map((fund, index) => renderFundRow(fund, index, true))}
              </TableBody>
            </Table>

            {renderMinAmountError('px-4 mt-3')}

            <div className='px-4 pt-4 pb-2'>
              <Button
                className='rounded-lg bg-primary-500 hover:bg-primary-400 disabled:bg-primary-200 w-full'
                onClick={handleContinue}
                loading={isLoading}
                disabled={hasLowRedemptionAmount}
              >
                Confirm
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </>
  )
}

export default MFRedeemBreakdown
